import { Link } from "@/lib/router-compat";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar } from "lucide-react";
import { categoryConfig } from "@/lib/categoryConfig";
import { getCurrentMonth, getCurrentSeason } from "@/lib/seasonalHelpers";

// Import category background images
import plantsImage from "@/assets/category-plants.webp";
import howToImage from "@/assets/category-how-to.webp";
import growGuidesImage from "@/assets/category-grow-guides.webp";
import flowersImage from "@/assets/category-flowers.webp";
import gardenDesignImage from "@/assets/category-garden-design.webp";
import reviewsImage from "@/assets/category-reviews.webp";
import seasonalGuideImage from "@/assets/category-seasonal-guide.webp";
import adviceImage from "@/assets/category-advice.webp";
import lawnMowerBanner from "@/assets/lawn-mower-banner.webp";

const categoryImages: Record<string, string> = {
  plants: plantsImage,
  "how-to": howToImage,
  "grow-guides": growGuidesImage,
  flowers: flowersImage,
  "garden-design": gardenDesignImage,
  reviews: reviewsImage,
  "seasonal-guide": seasonalGuideImage,
  "advice": adviceImage,
};

// Specific, benefit-led calls to action per category (avoids eight identical "Browse Guides" links)
const categoryCtas: Record<string, string> = {
  plants: "Find your next plant",
  "how-to": "Learn a new skill",
  "grow-guides": "Start growing",
  flowers: "Pick your blooms",
  "garden-design": "Get design ideas",
  advice: "Solve a garden problem",
  reviews: "See our top picks",
  "seasonal-guide": "Plan by season",
};

const CategoryGrid = () => {
  // Transform categoryConfig to display format
  const categories = Object.entries(categoryConfig)
    // The dynamic month card above already covers the seasonal guide
    .filter(([slug]) => slug !== "seasonal-guide")
    .map(([slug, config]) => ({
      slug,
      title: config.title,
      description: config.description,
      icon: config.icon,
      color: config.heroGradient,
      articles: config.sections.reduce((acc, section) => acc + section.links.length * 10, 0), // Approximate article count
    }));

  const currentMonth = getCurrentMonth();
  const currentSeason = getCurrentSeason();
  const monthSlug = currentMonth.toLowerCase();
  const seasonEmoji =
    currentSeason === "Spring" ? "🌱" : currentSeason === "Summer" ? "☀️" : currentSeason === "Autumn" ? "🍂" : "❄️";

  // Reviews leads the top row alongside the seasonal tile
  const leadCategory = categories.find((c) => c.slug === "reviews") ?? categories[0];
  const restCategories = categories.filter((c) => c.slug !== leadCategory?.slug);


  return (
    <section className="py-10 bg-muted/40">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="text-center mb-7">
          <h2 className="font-display text-3xl md:text-4xl font-semibold text-foreground mb-3 tracking-tight">
            Explore Our Garden Guides
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            From beginner basics to expert techniques, find everything you need
            to create and maintain your perfect garden.
          </p>
        </div>

        {/* Lead row: two larger editorial tiles set the rhythm */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Dynamic Seasonal Guide Card */}
          <Link to={`/category/seasonal-guide?sub=${monthSlug}`} className="group block h-full">
            <Card className="h-full overflow-hidden rounded-xl border-primary/30 ring-1 ring-primary/20 shadow-soft transition-shadow duration-300 hover:shadow-nature">
              <CardContent className="flex h-full flex-col p-0">
                <div className="relative aspect-[16/8] overflow-hidden">
                  {categoryImages["seasonal-guide"] && (
                    <img
                      src={categoryImages["seasonal-guide"]}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      width={800}
                      height={400}
                      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  )}
                  <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground shadow-md">
                    {seasonEmoji} {currentSeason} now
                  </span>
                </div>
                <div className="flex flex-1 flex-col gap-2 bg-card p-5 md:p-6">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    <Calendar className="h-4 w-4 text-primary" />
                    This month
                  </div>
                  <h3 className="font-display text-2xl font-semibold text-foreground">
                    {currentMonth} Garden Guide
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    What to do in your garden this {currentMonth}. Tasks, tips and planting advice for the month ahead.
                  </p>
                  <span className="mt-auto inline-flex items-center pt-3 text-sm font-semibold text-cta">
                    Read the {currentMonth} guide
                    <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </CardContent>
            </Card>
          </Link>

          {/* Lead category tile, same larger treatment */}
          {leadCategory && (() => {
            const IconComponent = leadCategory.icon;
            const backgroundImage = categoryImages[leadCategory.slug];
            return (
              <Link to={`/category/${leadCategory.slug}`} className="group block h-full">
                <Card className="h-full overflow-hidden rounded-xl border-border/60 shadow-soft transition-shadow duration-300 hover:shadow-nature">
                  <CardContent className="flex h-full flex-col p-0">
                    <div className="relative aspect-[16/8] overflow-hidden">
                      {backgroundImage && (
                        <img
                          src={backgroundImage}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          width={800}
                          height={400}
                          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      )}
                      <span className="absolute left-4 top-4 rounded-full bg-card/95 px-3 py-1 text-xs font-semibold text-foreground shadow-md">
                        {leadCategory.articles}+ articles
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col gap-2 bg-card p-5 md:p-6">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        <IconComponent className="h-4 w-4 text-primary" />
                        Hands-on testing
                      </div>

                      <h3 className="font-display text-2xl font-semibold text-foreground">
                        {leadCategory.title}
                      </h3>
                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {leadCategory.description}
                      </p>
                      <span className="mt-auto inline-flex items-center pt-3 text-sm font-semibold text-cta">
                        {categoryCtas[leadCategory.slug] ?? "Browse guides"}
                        <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })()}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {restCategories.map((category) => {
            const IconComponent = category.icon;
            const backgroundImage = categoryImages[category.slug];
            return (
              <Link key={category.slug} to={`/category/${category.slug}`} className="group block h-full">
                <Card className="h-full overflow-hidden rounded-xl border-border/60 shadow-soft transition-shadow duration-300 hover:shadow-nature">
                  <CardContent className="flex h-full flex-col p-0">
                    <div className="relative aspect-[16/9] overflow-hidden">
                      {backgroundImage && (
                        <img
                          src={backgroundImage}
                          alt=""
                          loading="lazy"
                          decoding="async"
                          width={600}
                          height={338}
                          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      )}
                      <span className="absolute left-4 top-4 rounded-full bg-card/95 px-3 py-1 text-xs font-semibold text-foreground shadow-md">
                        {category.articles}+ articles
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col gap-2 bg-card p-5">
                      <h3 className="flex items-center gap-2 font-display text-xl font-semibold text-foreground">
                        <IconComponent className="h-5 w-5 shrink-0 text-primary" />
                        {category.title}
                      </h3>

                      <p className="text-sm leading-relaxed text-muted-foreground">
                        {category.description}
                      </p>
                      <span className="mt-auto inline-flex items-center pt-3 text-sm font-semibold text-cta">
                        {categoryCtas[category.slug] ?? "Browse guides"}
                        <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>


        {/* Featured Lawn Mower Guide Banner */}
        <Link to="/best-lawn-mowers-uk" className="block mt-8 group">
          <div className="relative overflow-hidden rounded-xl min-h-[140px] flex items-center shadow-soft transition-all duration-300 group-hover:shadow-nature">
            <img
              src={lawnMowerBanner}
              alt=""
              loading="lazy"
              decoding="async"
              width={1200}
              height={300}
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-linear-to-r from-black/75 via-black/45 to-black/15" />
            <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 p-6 md:p-7">
              <div className="flex-1">
                <span className="inline-block text-xs font-semibold uppercase tracking-wider text-white/70 bg-white/10 px-3 py-1 rounded-full mb-2">
                  Featured Guide
                </span>
                <h3 className="text-xl md:text-2xl font-bold text-white mb-1.5">
                  Best Lawn Mowers UK 2026
                </h3>
                <p className="text-white/80 text-sm md:text-base max-w-xl">
                  Compare top-rated lawn mowers with our interactive buying guide. Filter by garden size, budget & mower type to find your perfect match.
                </p>
              </div>
              <Button
                size="lg"
                variant="secondary"
                className="shrink-0 text-base font-semibold gap-2 px-6 shadow-md group-hover:shadow-lg transition-shadow"
              >
                Explore the Guide
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
};

export default CategoryGrid;
